/**
 * Generic Catalog Page
 *
 * Mobile-first list page for catalogs: Providers, Families, Groups, Locations
 * Similar to ArticlesPage with navigation to detail pages.
 */

import { useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, Search, ChevronRight } from 'lucide-react'

import api from '@/lib/api'
import { debounce } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'
import { useUrlPage, useUrlState } from '@/hooks/useUrlState'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ExportButtons } from '@/components/ui/export-buttons'

interface CatalogItem {
  id: number
  code?: string
  name?: string
  description?: string
  contact_name?: string
  email?: string
  phone?: string
  address?: string
  [key: string]: string | number | undefined
}

interface CatalogConfig {
  endpoint: string
  basePath: string
  title: string
  newItemLabel: string
  noItemsLabel: string
  displayField: 'name' | 'description'
  secondaryField?: string
  tertiaryField?: string
  columns: {
    key: string
    header: string
    format?: (value: unknown) => string
  }[]
}

interface CatalogPageProps {
  config: CatalogConfig
  icon: React.ComponentType<{ className?: string }>
}

export function CatalogPage({ config, icon: Icon }: CatalogPageProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useUrlPage()
  const [searchQuery, setSearchQuery] = useUrlState('q')
  const [searchInput, setSearchInput] = useState(searchQuery)

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value)
    }, 300),
    []
  )

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSearch(value)
  }

  // Fetch items
  const { data, isLoading } = useQuery({
    queryKey: [config.endpoint, page, searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('page', page.toString())
      params.set('page_size', '20')
      if (searchQuery) params.set('search', searchQuery)
      const response = await api.get(`/catalogs/${config.endpoint}?${params}`)
      return response.data
    },
  })

  const getDisplayValue = (item: CatalogItem) => {
    return item[config.displayField] || item.contact_name || item.code || `#${item.id}`
  }

  const getSecondaryValue = (item: CatalogItem) => {
    if (!config.secondaryField) return null
    return item[config.secondaryField]
  }

  return (
    <div className="space-y-4">
      {/* Header - Mobile optimized */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-3xl font-bold">{config.title}</h1>
          <p className="text-sm text-muted-foreground hidden md:block">
            {data?.total || 0} {t('common.records')}
          </p>
        </div>
        <div className="flex gap-2">
          <ExportButtons
            data={data?.items || []}
            filename={config.endpoint}
            columns={config.columns}
          />
          <Button onClick={() => navigate(`${config.basePath}/new`)} className="hidden md:flex">
            <Plus className="h-4 w-4 mr-2" />
            {config.newItemLabel}
          </Button>
        </div>
      </div>

      {/* Search - Touch friendly */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input
          placeholder={t('common.search')}
          className="pl-10 h-12 text-base"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : data?.items?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Icon className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium">{config.noItemsLabel}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {searchQuery ? t('common.noResults') : t('catalogs.addFirst')}
          </p>
          {!searchQuery && (
            <Button onClick={() => navigate(`${config.basePath}/new`)}>
              <Plus className="h-4 w-4 mr-2" />
              {config.newItemLabel}
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile: Cards */}
          <div className="space-y-3 lg:hidden">
            {data?.items?.map((item: CatalogItem) => (
              <Link
                key={item.id}
                to={`${config.basePath}/${item.id}`}
                className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
              <Card
                className="active:bg-muted/50 cursor-pointer touch-manipulation"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {getDisplayValue(item)}
                      </p>
                      {getSecondaryValue(item) && (
                        <p className="text-sm text-muted-foreground truncate">
                          {getSecondaryValue(item)}
                        </p>
                      )}
                      {config.tertiaryField && item[config.tertiaryField] && (
                        <p className="text-xs text-muted-foreground truncate">
                          {item[config.tertiaryField]}
                        </p>
                      )}
                      {item.phone && (
                        <p className="text-xs text-muted-foreground">
                          {item.phone}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                  </div>
                </CardContent>
              </Card>
              </Link>
            ))}
          </div>

          {/* Desktop: Table */}
          <Card className="hidden lg:block">
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    {config.columns.map((col) => (
                      <th key={col.key} className="text-left p-4 font-medium">
                        {col.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data?.items?.map((item: CatalogItem) => (
                    <tr
                      key={item.id}
                      className="border-b hover:bg-muted/50 cursor-pointer focus-visible:outline-none focus-visible:bg-muted/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                      onClick={() => navigate(`${config.basePath}/${item.id}`)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') navigate(`${config.basePath}/${item.id}`)
                      }}
                    >
                      {config.columns.map((col) => (
                        <td key={col.key} className="p-4">
                          {col.format
                            ? col.format(item[col.key])
                            : item[col.key]?.toString() || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Pagination */}
          {data && data.pages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <p className="text-sm text-muted-foreground">
                {t('common.page')} {data.page} {t('common.of')} {data.pages}
              </p>
              <Pagination
                currentPage={page}
                totalPages={data.pages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}

      {/* Mobile FAB for New Item */}
      <Button
        onClick={() => navigate(`${config.basePath}/new`)}
        className="md:hidden fixed bottom-20 right-4 h-14 w-14 rounded-full shadow-lg z-10"
        size="icon"
      >
        <Plus className="h-6 w-6" />
      </Button>
    </div>
  )
}

export type { CatalogConfig, CatalogItem }
